import React, { useEffect, useRef, useState } from 'react';
import { supabase } from '../../lib/supabase/client';
import { toSantiagoDateStr } from '../../lib/utils/dateUtils';
import { downloadCSV, resolveExportRange, type ExportRangeKind } from '../../lib/utils/csvExport';
import './CobrosView.css';

// ============================================================================
// TIPOS
// ============================================================================

export interface CobrosViewProps {
  therapistId: string;
  isDirector: boolean;
  inversionistaId?: string;
}

interface CobroRow {
  consulta_id: string;
  pago_id: string | null;
  paciente_nombre: string;
  paciente_apellido: string;
  paciente_rut: string | null;
  terapeuta_id: string;
  terapeuta_nombre: string;
  terapeuta_apellido: string;
  fecha_hora: string;
  tipo_consulta: string;
  monto_final: number | null;
  metodo_pago: string | null;
  estado_pago: string | null;
  voucher_storage_path: string | null;
  voucher_registrado_at: string | null;
  estado_boleta: string | null;
  numero_boleta_sii: string | null;
  banco: string | null;
  numero_comprobante: string | null;
}

interface OcrResult {
  monto: number | null;
  banco_destino: string | null;
  banco_origen: string | null;
  fecha: string | null;
  numero_transferencia: string | null;
  rut_origen: string | null;
  nombre_origen: string | null;
}

interface OcrConfirmState {
  pagoId: string;
  voucherPath: string;
  monto: string;
  banco: string;
  numeroComprobante: string;
  fechaPago: string;
  ocrWarning: string | null;
}

interface SiiModalState {
  cobro: CobroRow;
}

interface BoletaModalState {
  pagoId: string;
  numeroBoletaInput: string;
  pdfFile: File | null;
  submitting: boolean;
}

// ============================================================================
// HELPERS
// ============================================================================

const pad2 = (n: number) => String(n).padStart(2, '0');

const toISODate = (d: Date) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const fmtCLP = (n: number | null) =>
  n == null
    ? '—'
    : new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(n);

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString('es-CL', { day: 'numeric', month: 'short', year: 'numeric' });

const METODO_LABEL: Record<string, string> = {
  efectivo: 'Efectivo',
  transferencia: 'Transferencia',
  tarjeta_debito: 'Débito',
  tarjeta_credito: 'Crédito',
  webpay_debito: 'Webpay débito',
  webpay_credito: 'Webpay crédito',
  cuotas_sin_interes: 'Cuotas s/i',
};

const TIPO_LABEL: Record<string, string> = {
  individual: 'Individual',
  pareja: 'Pareja',
  familiar: 'Familiar',
};

const ESTADO_PAGO_LABEL: Record<string, string> = {
  pendiente: 'Pendiente',
  pagado: 'Pagado',
  parcial: 'Parcial',
  reembolsado: 'Reembolsado',
};

// ============================================================================
// SUB-COMPONENTES
// ============================================================================

const Chip: React.FC<{ variant: 'green' | 'orange' | 'gray' | 'blue'; children: React.ReactNode }> = ({
  variant,
  children,
}) => <span className={`cobros-chip cobros-chip--${variant}`}>{children}</span>;

const CopyField: React.FC<{ label: string; value: string }> = ({ label, value }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="cobros-copy-field">
      <span className="cobros-copy-field__label">{label}</span>
      <div className="cobros-copy-field__row">
        <span className="cobros-copy-field__value">{value}</span>
        <button
          type="button"
          className="cobros-copy-btn"
          onClick={handleCopy}
          title="Copiar"
        >
          <i className={copied ? 'fas fa-check' : 'fas fa-copy'} />
        </button>
      </div>
    </div>
  );
};

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

const CobrosView: React.FC<CobrosViewProps> = ({ therapistId, isDirector, inversionistaId }) => {
  const defaultFechaInicio = toISODate(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const defaultFechaFin = toISODate(new Date());

  const [fechaInicio, setFechaInicio] = useState(defaultFechaInicio);
  const [fechaFin, setFechaFin] = useState(defaultFechaFin);
  const [filtroTerapeuta, setFiltroTerapeuta] = useState('todos');

  const [cobros, setCobros] = useState<CobroRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const [uploadingForPagoId, setUploadingForPagoId] = useState<string | null>(null);
  const [ocrConfirm, setOcrConfirm] = useState<OcrConfirmState | null>(null);
  const [siiModal, setSiiModal] = useState<SiiModalState | null>(null);
  const [boletaModal, setBoletaModal] = useState<BoletaModalState | null>(null);

  // Sandbox rol inversor — sync manual a Google Drive (ver upload-to-drive)
  const [driveSynced, setDriveSynced] = useState<Record<string, { voucher?: string; boleta?: string }>>({});
  const [drivePending, setDrivePending] = useState<string | null>(null);

  // Exportar CSV — selector de rango (semana/mes/mes anterior/personalizado)
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [exportingRange, setExportingRange] = useState(false);
  const [exportCustomDesde, setExportCustomDesde] = useState('');
  const [exportCustomHasta, setExportCustomHasta] = useState('');
  const exportMenuRef = useRef<HTMLDivElement>(null);

  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  // ── Carga de datos ──────────────────────────────────────────────────────────

  const loadCobros = async () => {
    setLoading(true);
    setErrorMsg(null);

    let result;
    if (isDirector) {
      result = await supabase.rpc('get_cobros_director', {
        p_fecha_inicio: fechaInicio,
        p_fecha_fin: fechaFin,
        p_terapeuta_id: filtroTerapeuta !== 'todos' ? filtroTerapeuta : null,
      });
    } else if (inversionistaId) {
      result = await supabase.rpc('get_cobros_inversionista', {
        p_inversionista_id: inversionistaId,
        p_fecha_inicio: fechaInicio,
        p_fecha_fin: fechaFin,
      });
    } else {
      setLoading(false);
      return;
    }

    const { data, error } = result;
    if (error) {
      console.error('[CobrosView] Error cargando cobros:', error.code);
      setErrorMsg('No se pudieron cargar los cobros. Intentá nuevamente.');
    } else {
      setCobros((data as CobroRow[]) ?? []);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadCobros();
  }, [fechaInicio, fechaFin, filtroTerapeuta, therapistId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cierra el menú de exportar al clickear afuera
  useEffect(() => {
    if (!showExportMenu) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target as Node)) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showExportMenu]);

  // ── Exportar CSV ────────────────────────────────────────────────────────────
  // Consulta independiente del rango on-screen (fechaInicio/fechaFin): el
  // selector de exportación define su propio rango (semana/mes/mes anterior/
  // personalizado) sin alterar los filtros que el usuario ya tiene puestos en
  // pantalla. Respeta el filtro de terapeuta activo, igual que la tabla visible.
  const handleExportCobros = async (kind: ExportRangeKind, custom?: { desde: string; hasta: string }) => {
    let range;
    try {
      range = resolveExportRange(kind, toSantiagoDateStr(new Date()), custom);
    } catch {
      setErrorMsg('Seleccioná ambas fechas del rango personalizado.');
      return;
    }
    if (range.desde > range.hasta) {
      setErrorMsg('La fecha "desde" no puede ser posterior a "hasta".');
      return;
    }

    setShowExportMenu(false);
    setExportingRange(true);
    setErrorMsg(null);

    try {
      let result;
      if (isDirector) {
        result = await supabase.rpc('get_cobros_director', {
          p_fecha_inicio: range.desde,
          p_fecha_fin: range.hasta,
          p_terapeuta_id: filtroTerapeuta !== 'todos' ? filtroTerapeuta : null,
        });
      } else if (inversionistaId) {
        result = await supabase.rpc('get_cobros_inversionista', {
          p_inversionista_id: inversionistaId,
          p_fecha_inicio: range.desde,
          p_fecha_fin: range.hasta,
        });
      } else {
        setExportingRange(false);
        return;
      }

      const { data, error } = result;
      if (error) {
        console.error('[CobrosView] Error exportando cobros:', error.code);
        setErrorMsg('No se pudo generar el archivo. Intentá nuevamente.');
        return;
      }

      const rows = (data as CobroRow[]) ?? [];
      if (rows.length === 0) {
        setErrorMsg('No hay cobros en el rango seleccionado para exportar.');
        return;
      }

      const headers = ['Fecha', 'Paciente', 'RUT', 'Terapeuta', 'Tipo', 'Monto', 'Método de pago', 'Estado de pago'];
      const csvRows = rows.map(c => [
        toSantiagoDateStr(new Date(c.fecha_hora)),
        `${c.paciente_nombre} ${c.paciente_apellido}`,
        c.paciente_rut ?? '',
        `${c.terapeuta_nombre} ${c.terapeuta_apellido}`,
        TIPO_LABEL[c.tipo_consulta] ?? c.tipo_consulta,
        c.monto_final ?? '',
        c.metodo_pago ? (METODO_LABEL[c.metodo_pago] ?? c.metodo_pago) : '',
        c.pago_id ? (ESTADO_PAGO_LABEL[c.estado_pago ?? ''] ?? c.estado_pago ?? '') : 'Sin pago',
      ]);

      downloadCSV(`cobros_${range.desde}_${range.hasta}.csv`, headers, csvRows);
    } catch {
      setErrorMsg('Error inesperado al generar el archivo. Intentá nuevamente.');
    } finally {
      setExportingRange(false);
    }
  };

  // ── Upload + OCR ────────────────────────────────────────────────────────────

  const handleFileSelect = async (pagoId: string, file: File) => {
    setUploadingForPagoId(pagoId);

    try {
      const now = new Date();
      const year = now.getFullYear();
      const month = pad2(now.getMonth() + 1);
      const ext = file.name.split('.').pop() ?? 'jpg';
      const storagePath = `${year}/${month}/${pagoId}.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from('vouchers')
        .upload(storagePath, file, { upsert: true });

      if (uploadError) {
        console.error('[CobrosView] Storage upload error:', uploadError.message);
        setErrorMsg('Error al subir el voucher. Verificá el tamaño y formato del archivo.');
        setUploadingForPagoId(null);
        return;
      }

      // Convertir imagen a base64 para el OCR
      const arrayBuffer = await file.arrayBuffer();
      const bytes = new Uint8Array(arrayBuffer);
      let binary = '';
      for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
      }
      const base64 = btoa(binary);

      let ocrWarning: string | null = null;
      let ocrData: OcrResult = {
        monto: null,
        banco_destino: null,
        banco_origen: null,
        fecha: null,
        numero_transferencia: null,
        rut_origen: null,
        nombre_origen: null,
      };

      const { data: fnData, error: fnError } = await supabase.functions.invoke('analyze-voucher', {
        body: { image_base64: base64, media_type: file.type },
      });

      if (fnError || !fnData?.success) {
        ocrWarning = 'No se pudo leer el voucher automáticamente. Completá los datos manualmente.';
      } else {
        ocrData = fnData.data as OcrResult;
      }

      const today = toISODate(new Date());
      setOcrConfirm({
        pagoId,
        voucherPath: storagePath,
        monto: ocrData.monto != null ? String(ocrData.monto) : '',
        banco: ocrData.banco_destino ?? '',
        numeroComprobante: ocrData.numero_transferencia ?? '',
        fechaPago: ocrData.fecha ?? today,
        ocrWarning,
      });
    } catch {
      setErrorMsg('Error inesperado al procesar el voucher. Intentá nuevamente.');
    } finally {
      setUploadingForPagoId(null);
    }
  };

  // ── Confirmar datos OCR ──────────────────────────────────────────────────────

  const handleConfirmOcr = async () => {
    if (!ocrConfirm) return;

    const montoNum = parseFloat(ocrConfirm.monto.replace(/\./g, '').replace(',', '.'));
    if (isNaN(montoNum) || montoNum <= 0) {
      setErrorMsg('El monto debe ser un número válido mayor a cero.');
      return;
    }

    const { error } = await supabase
      .from('pagos')
      .update({
        voucher_storage_path: ocrConfirm.voucherPath,
        voucher_registrado_at: new Date().toISOString(),
        voucher_registrado_por: isDirector ? therapistId : null,
        banco: ocrConfirm.banco || null,
        numero_comprobante: ocrConfirm.numeroComprobante || null,
        fecha_pago: ocrConfirm.fechaPago ? new Date(ocrConfirm.fechaPago + 'T12:00:00').toISOString() : null,
        estado_pago: 'pagado',
        monto_final: montoNum,
        ocr_datos_crudos: null,
      })
      .eq('id', ocrConfirm.pagoId);

    if (error) {
      console.error('[CobrosView] Error confirmando pago:', error.code);
      setErrorMsg('Error al guardar el pago. Verificá tus permisos.');
      return;
    }

    setOcrConfirm(null);
    await loadCobros();
  };

  // ── Registrar boleta ────────────────────────────────────────────────────────

  const handleRegistrarBoleta = async () => {
    if (!boletaModal || !boletaModal.numeroBoletaInput.trim()) return;

    setBoletaModal(prev => prev ? { ...prev, submitting: true } : null);

    let pdfPath: string | null = null;

    if (boletaModal.pdfFile) {
      const ext = boletaModal.pdfFile.name.split('.').pop() ?? 'pdf';
      const now = new Date();
      const storagePath = `boletas/${now.getFullYear()}/${pad2(now.getMonth() + 1)}/${boletaModal.pagoId}.${ext}`;
      const { error: uploadErr } = await supabase.storage
        .from('vouchers')
        .upload(storagePath, boletaModal.pdfFile, { upsert: true });
      if (!uploadErr) {
        pdfPath = storagePath;
      }
    }

    const { data: ok, error } = await supabase.rpc('registrar_boleta_manual', {
      p_pago_id: boletaModal.pagoId,
      p_numero_boleta: boletaModal.numeroBoletaInput.trim(),
      p_pdf_path: pdfPath,
    });

    if (error || !ok) {
      console.error('[CobrosView] Error registrando boleta:', error?.code);
      setErrorMsg('Error al registrar la boleta. Verificá tus permisos.');
      setBoletaModal(prev => prev ? { ...prev, submitting: false } : null);
      return;
    }

    setBoletaModal(null);
    await loadCobros();
  };

  // ── Sync a Google Drive (solo rol inversor) ─────────────────────────────────

  const handleEnviarADrive = async (pagoId: string, tipo: 'voucher' | 'boleta') => {
    const key = `${pagoId}:${tipo}`;
    setDrivePending(key);

    try {
      const { data, error } = await supabase.functions.invoke('upload-to-drive', {
        body: { pago_id: pagoId, tipo },
      });

      if (error || !data?.success) {
        console.error('[CobrosView] Error enviando a Drive:', error?.message ?? data?.error);
        setErrorMsg(data?.error ?? 'No se pudo enviar a Drive. Intentá nuevamente.');
        return;
      }

      setDriveSynced(prev => ({
        ...prev,
        [pagoId]: { ...prev[pagoId], [tipo]: data.drive_file_id },
      }));
    } catch {
      setErrorMsg('Error inesperado al enviar a Drive.');
    } finally {
      setDrivePending(null);
    }
  };

  const renderDriveButton = (pagoId: string, tipo: 'voucher' | 'boleta') => {
    if (isDirector) return null;

    const synced = driveSynced[pagoId]?.[tipo];
    if (synced) {
      return (
        <Chip variant="blue">
          <i className="fab fa-google-drive" /> En Drive
        </Chip>
      );
    }

    const pending = drivePending === `${pagoId}:${tipo}`;
    return (
      <button
        type="button"
        className="cobros-action-btn cobros-action-btn--secondary"
        disabled={pending}
        onClick={() => handleEnviarADrive(pagoId, tipo)}
      >
        {pending ? (
          <><i className="fas fa-spinner fa-spin" /> Enviando...</>
        ) : (
          <><i className="fab fa-google-drive" /> Enviar a Drive</>
        )}
      </button>
    );
  };

  // ── Render helpers ──────────────────────────────────────────────────────────

  const handleVerVoucher = async (storagePath: string) => {
    const { data, error } = await supabase.storage.from('vouchers').createSignedUrl(storagePath, 60);
    if (error || !data?.signedUrl) {
      console.error('[CobrosView] Error generando signed URL:', error?.message);
      setErrorMsg('No se pudo abrir el voucher. Intentá nuevamente.');
      return;
    }
    window.open(data.signedUrl, '_blank', 'noopener,noreferrer');
  };

  const renderVoucherCell = (cobro: CobroRow) => {
    if (!cobro.pago_id) return <Chip variant="gray">Sin pago</Chip>;

    if (cobro.voucher_storage_path) {
      return (
        <div className="cobros-cell-actions">
          <button
            type="button"
            className="cobros-link-btn"
            onClick={() => handleVerVoucher(cobro.voucher_storage_path!)}
          >
            <i className="fas fa-eye" /> Ver
          </button>
          <Chip variant="green">
            <i className="fas fa-check" /> Registrado
          </Chip>
          {renderDriveButton(cobro.pago_id!, 'voucher')}
        </div>
      );
    }

    if (cobro.metodo_pago === 'transferencia') {
      const isUploading = uploadingForPagoId === cobro.pago_id;
      return (
        <div className="cobros-cell-actions">
          <input
            type="file"
            accept="image/*"
            className="cobros-file-input"
            ref={el => { fileInputRefs.current[cobro.pago_id!] = el; }}
            onChange={e => {
              const file = e.target.files?.[0];
              if (file) handleFileSelect(cobro.pago_id!, file);
              e.target.value = '';
            }}
          />
          <button
            type="button"
            className="cobros-action-btn cobros-action-btn--primary"
            disabled={isUploading}
            onClick={() => fileInputRefs.current[cobro.pago_id!]?.click()}
          >
            {isUploading ? (
              <><i className="fas fa-spinner fa-spin" /> Procesando...</>
            ) : (
              <><i className="fas fa-upload" /> Subir voucher</>
            )}
          </button>
        </div>
      );
    }

    return <Chip variant="gray">N/A</Chip>;
  };

  const renderBoletaCell = (cobro: CobroRow) => {
    if (!cobro.pago_id) return null;

    if (cobro.estado_boleta === 'emitida') {
      return (
        <div className="cobros-cell-actions">
          <Chip variant="green">
            <i className="fas fa-file-invoice" /> #{cobro.numero_boleta_sii}
          </Chip>
          {renderDriveButton(cobro.pago_id!, 'boleta')}
        </div>
      );
    }

    if (cobro.estado_boleta === 'no_aplica') {
      return <Chip variant="gray">N/A</Chip>;
    }

    // pendiente o null
    return (
      <button
        type="button"
        className="cobros-action-btn cobros-action-btn--secondary"
        onClick={() => setSiiModal({ cobro })}
      >
        <i className="fas fa-file-invoice-dollar" /> Emitir
      </button>
    );
  };

  // ── Render principal ────────────────────────────────────────────────────────

  return (
    <div className="cobros-view">
      <div className="cobros-header">
        <div>
          <h2 className="cobros-title">
            {isDirector ? 'Cobros y Boletas' : 'Cobros'}
          </h2>
          <p className="cobros-subtitle">
            {isDirector
              ? 'Gestión de pagos por transferencia y emisión de boletas SII'
              : 'Cobros de tu cartera de pacientes'}
          </p>
        </div>

        <div className="cobros-export-wrapper" ref={exportMenuRef}>
          <button
            type="button"
            className="cobros-export-btn"
            disabled={exportingRange}
            onClick={() => setShowExportMenu(v => !v)}
          >
            <i className={`fas ${exportingRange ? 'fa-spinner fa-spin' : 'fa-download'}`} />
            Exportar
          </button>

          {showExportMenu && (
            <div className="cobros-export-menu">
              <button
                type="button"
                className="cobros-export-menu-item"
                onClick={() => handleExportCobros('semana')}
              >
                <i className="fas fa-calendar-week" />
                Esta semana
              </button>
              <button
                type="button"
                className="cobros-export-menu-item"
                onClick={() => handleExportCobros('mes')}
              >
                <i className="fas fa-calendar" />
                Este mes
              </button>
              <button
                type="button"
                className="cobros-export-menu-item"
                onClick={() => handleExportCobros('mes_anterior')}
              >
                <i className="fas fa-calendar-minus" />
                Mes anterior
              </button>

              <div className="cobros-export-menu-divider" />

              <div className="cobros-export-menu-custom">
                <span className="cobros-export-menu-custom__label">Rango personalizado</span>
                <div className="cobros-export-menu-custom__row">
                  <input
                    type="date"
                    className="cobros-input cobros-input--sm"
                    value={exportCustomDesde}
                    onChange={e => setExportCustomDesde(e.target.value)}
                    aria-label="Fecha desde"
                  />
                  <span className="cobros-export-menu-custom__sep">a</span>
                  <input
                    type="date"
                    className="cobros-input cobros-input--sm"
                    value={exportCustomHasta}
                    onChange={e => setExportCustomHasta(e.target.value)}
                    aria-label="Fecha hasta"
                  />
                </div>
                <button
                  type="button"
                  className="cobros-btn cobros-btn--primary cobros-export-menu-custom__btn"
                  disabled={!exportCustomDesde || !exportCustomHasta || exportingRange}
                  onClick={() =>
                    handleExportCobros('personalizado', { desde: exportCustomDesde, hasta: exportCustomHasta })
                  }
                >
                  <i className="fas fa-download" />
                  Exportar rango
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Filtros ── */}
      <div className="cobros-filters">
        <div className="cobros-filter-group">
          <label className="cobros-filter-label">Desde</label>
          <input
            type="date"
            className="cobros-input"
            value={fechaInicio}
            onChange={e => setFechaInicio(e.target.value)}
          />
        </div>
        <div className="cobros-filter-group">
          <label className="cobros-filter-label">Hasta</label>
          <input
            type="date"
            className="cobros-input"
            value={fechaFin}
            onChange={e => setFechaFin(e.target.value)}
          />
        </div>
        {isDirector && (
          <div className="cobros-filter-group">
            <label className="cobros-filter-label">Terapeuta</label>
            <select
              className="cobros-input"
              value={filtroTerapeuta}
              onChange={e => setFiltroTerapeuta(e.target.value)}
            >
              <option value="todos">Todos</option>
              {Array.from(
                new Map(cobros.map(c => [c.terapeuta_id, c])).values()
              ).map(c => (
                <option
                  key={c.terapeuta_id}
                  value={c.terapeuta_id}
                >
                  {c.terapeuta_nombre} {c.terapeuta_apellido}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* ── Error global ── */}
      {errorMsg && (
        <div className="cobros-error-banner" role="alert">
          <i className="fas fa-exclamation-circle" />
          <span>{errorMsg}</span>
          <button type="button" onClick={() => setErrorMsg(null)} className="cobros-error-close">
            <i className="fas fa-times" />
          </button>
        </div>
      )}

      {/* ── Tabla ── */}
      <div className="cobros-table-wrapper">
        {loading ? (
          <div className="cobros-loading">
            <div className="spinner" />
            <p>Cargando cobros...</p>
          </div>
        ) : cobros.length === 0 ? (
          <div className="cobros-empty">
            <i className="fas fa-receipt" />
            <p>No hay cobros en el período seleccionado.</p>
          </div>
        ) : (
          <table className="cobros-table">
            <thead>
              <tr>
                <th>Paciente</th>
                <th>Fecha</th>
                {isDirector && <th>Terapeuta</th>}
                <th>Tipo</th>
                <th className="cobros-th--right">Monto</th>
                <th>Método</th>
                <th>Voucher</th>
                <th>Boleta</th>
              </tr>
            </thead>
            <tbody>
              {cobros.map(cobro => (
                <tr key={cobro.consulta_id} className="cobros-tr">
                  <td>
                    <div className="cobros-patient-name">
                      {cobro.paciente_nombre} {cobro.paciente_apellido}
                    </div>
                    {cobro.paciente_rut && (
                      <div className="cobros-patient-rut">{cobro.paciente_rut}</div>
                    )}
                  </td>
                  <td className="cobros-td--nowrap">{fmtDate(cobro.fecha_hora)}</td>
                  {isDirector && (
                    <td className="cobros-td--nowrap">
                      {cobro.terapeuta_nombre} {cobro.terapeuta_apellido}
                    </td>
                  )}
                  <td>{TIPO_LABEL[cobro.tipo_consulta] ?? cobro.tipo_consulta}</td>
                  <td className="cobros-td--right cobros-td--monto">
                    {fmtCLP(cobro.monto_final)}
                  </td>
                  <td>
                    {cobro.metodo_pago
                      ? METODO_LABEL[cobro.metodo_pago] ?? cobro.metodo_pago
                      : '—'}
                  </td>
                  <td>{renderVoucherCell(cobro)}</td>
                  <td>{renderBoletaCell(cobro)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Modal confirmación OCR ── */}
      {ocrConfirm && (
        <div className="cobros-modal-overlay" onClick={() => setOcrConfirm(null)}>
          <div className="cobros-modal" onClick={e => e.stopPropagation()}>
            <div className="cobros-modal__header">
              <h3>Confirmar datos del voucher</h3>
              <button type="button" className="cobros-modal__close" onClick={() => setOcrConfirm(null)}>
                <i className="fas fa-times" />
              </button>
            </div>

            {ocrConfirm.ocrWarning && (
              <div className="cobros-ocr-warning">
                <i className="fas fa-exclamation-triangle" />
                <span>{ocrConfirm.ocrWarning}</span>
              </div>
            )}

            <div className="cobros-modal__body">
              <div className="cobros-form-group">
                <label className="cobros-form-label">Monto (CLP)</label>
                <input
                  type="number"
                  className="cobros-input"
                  placeholder="Ej: 45000"
                  value={ocrConfirm.monto}
                  onChange={e => setOcrConfirm(prev => prev ? { ...prev, monto: e.target.value } : null)}
                />
              </div>
              <div className="cobros-form-group">
                <label className="cobros-form-label">Banco destino</label>
                <input
                  type="text"
                  className="cobros-input"
                  placeholder="Ej: Banco Santander"
                  value={ocrConfirm.banco}
                  onChange={e => setOcrConfirm(prev => prev ? { ...prev, banco: e.target.value } : null)}
                />
              </div>
              <div className="cobros-form-group">
                <label className="cobros-form-label">Número de transferencia</label>
                <input
                  type="text"
                  className="cobros-input"
                  placeholder="Número o código de la operación"
                  value={ocrConfirm.numeroComprobante}
                  onChange={e => setOcrConfirm(prev => prev ? { ...prev, numeroComprobante: e.target.value } : null)}
                />
              </div>
              <div className="cobros-form-group">
                <label className="cobros-form-label">Fecha de pago</label>
                <input
                  type="date"
                  className="cobros-input"
                  value={ocrConfirm.fechaPago}
                  onChange={e => setOcrConfirm(prev => prev ? { ...prev, fechaPago: e.target.value } : null)}
                />
              </div>
            </div>

            <div className="cobros-modal__footer">
              <button
                type="button"
                className="cobros-btn cobros-btn--ghost"
                onClick={() => setOcrConfirm(null)}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="cobros-btn cobros-btn--primary"
                onClick={handleConfirmOcr}
              >
                <i className="fas fa-check" /> Confirmar pago
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal datos SII ── */}
      {siiModal && (
        <div className="cobros-modal-overlay" onClick={() => setSiiModal(null)}>
          <div className="cobros-modal" onClick={e => e.stopPropagation()}>
            <div className="cobros-modal__header">
              <h3>Datos para emitir en SII</h3>
              <button type="button" className="cobros-modal__close" onClick={() => setSiiModal(null)}>
                <i className="fas fa-times" />
              </button>
            </div>

            <p className="cobros-modal__hint">
              Copiá estos datos para ingresarlos en el portal del SII al emitir la boleta electrónica.
            </p>

            <div className="cobros-modal__body cobros-modal__body--copy">
              <CopyField label="RUT contribuyente" value="23683221-K" />
              <CopyField
                label="Monto"
                value={siiModal.cobro.monto_final != null
                  ? String(Math.round(siiModal.cobro.monto_final))
                  : '—'}
              />
              <CopyField
                label="RUT receptor"
                value={siiModal.cobro.paciente_rut ?? '—'}
              />
              <CopyField
                label="Nombre receptor"
                value={`${siiModal.cobro.paciente_nombre} ${siiModal.cobro.paciente_apellido}`}
              />
              <CopyField label="Ciudad" value="Santiago" />
              <CopyField
                label="Detalle"
                value={`Atención Psicológica ${siiModal.cobro.terapeuta_nombre} ${siiModal.cobro.terapeuta_apellido}`}
              />
            </div>

            <div className="cobros-modal__footer">
              <button
                type="button"
                className="cobros-btn cobros-btn--ghost"
                onClick={() => setSiiModal(null)}
              >
                Cerrar
              </button>
              {siiModal.cobro.pago_id && (
                <button
                  type="button"
                  className="cobros-btn cobros-btn--primary"
                  onClick={() => {
                    const pagoId = siiModal.cobro.pago_id!;
                    setSiiModal(null);
                    setBoletaModal({ pagoId, numeroBoletaInput: '', pdfFile: null, submitting: false });
                  }}
                >
                  <i className="fas fa-arrow-right" /> Ya emití — ingresar número
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Modal ingresar número de boleta ── */}
      {boletaModal && (
        <div className="cobros-modal-overlay" onClick={() => !boletaModal.submitting && setBoletaModal(null)}>
          <div className="cobros-modal" onClick={e => e.stopPropagation()}>
            <div className="cobros-modal__header">
              <h3>Ingresar número de boleta</h3>
              <button
                type="button"
                className="cobros-modal__close"
                disabled={boletaModal.submitting}
                onClick={() => setBoletaModal(null)}
              >
                <i className="fas fa-times" />
              </button>
            </div>

            <div className="cobros-modal__body">
              <div className="cobros-form-group">
                <label className="cobros-form-label">Número de boleta SII *</label>
                <input
                  type="text"
                  className="cobros-input"
                  placeholder="Ej: 12345"
                  value={boletaModal.numeroBoletaInput}
                  disabled={boletaModal.submitting}
                  onChange={e =>
                    setBoletaModal(prev => prev ? { ...prev, numeroBoletaInput: e.target.value } : null)
                  }
                />
              </div>
              <div className="cobros-form-group">
                <label className="cobros-form-label">PDF de la boleta (opcional)</label>
                <input
                  type="file"
                  accept=".pdf,image/*"
                  className="cobros-input cobros-input--file"
                  disabled={boletaModal.submitting}
                  onChange={e =>
                    setBoletaModal(prev =>
                      prev ? { ...prev, pdfFile: e.target.files?.[0] ?? null } : null
                    )
                  }
                />
                {boletaModal.pdfFile && (
                  <span className="cobros-file-name">{boletaModal.pdfFile.name}</span>
                )}
              </div>
            </div>

            <div className="cobros-modal__footer">
              <button
                type="button"
                className="cobros-btn cobros-btn--ghost"
                disabled={boletaModal.submitting}
                onClick={() => setBoletaModal(null)}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="cobros-btn cobros-btn--primary"
                disabled={boletaModal.submitting || !boletaModal.numeroBoletaInput.trim()}
                onClick={handleRegistrarBoleta}
              >
                {boletaModal.submitting ? (
                  <><i className="fas fa-spinner fa-spin" /> Guardando...</>
                ) : (
                  <><i className="fas fa-save" /> Guardar boleta</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CobrosView;
